import { Course } from '../courses/types';

export const CSHARP_COURSES: Course[] = [
  {
    id: 'csharp-advanced',
    title: 'Advanced C# Programming',
    description: 'Deep dive into advanced C# concepts, async programming, and .NET Core',
    level: 'Advanced',
    duration: '30 hours',
    totalDuration: '30 hours',
    imageSrc: '/api/placeholder/400/300',
    requirements: ['C# fundamentals', 'Object-oriented programming', 'Basic .NET knowledge'],
    learningOutcomes: [
      'Master advanced C# language features',
      'Implement async/await patterns effectively',
      'Work with LINQ and delegates',
      'Build performance-optimized applications',
      'Handle memory management and garbage collection'
    ],
    modules: [
      {
        id: 'csharp-generics',
        title: 'Generics and Collections',
        description: 'Master generic programming and advanced collection types',
        content: `## Generics in C#

Learn to create type-safe, reusable code with C# generics.

### Generic Concepts:
- Type parameters and constraints
- Generic methods and classes
- Covariance and contravariance
- Generic interfaces

### Advanced Collections:
- Dictionary<TKey, TValue>
- List<T> vs Array performance
- HashSet<T> for unique collections
- Queue<T> and Stack<T>
- Custom collection implementations

### Constraints:
\`\`\`csharp
public class Repository<T> where T : class, IEntity, new()
{
    public T Create() => new T();
    public void Save(T entity) { /* implementation */ }
}
\`\`\``,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 1
      },
      {
        id: 'csharp-delegates',
        title: 'Delegates, Events, and Lambda Expressions',
        description: 'Master functional programming concepts in C#',
        content: `## Delegates and Events

Understand the foundation of event-driven programming in C#.

### Delegate Types:
- Action and Func delegates
- Multicast delegates
- Anonymous methods
- Lambda expressions

### Events:
- Event declaration and subscription
- EventHandler and custom event args
- Event accessor methods
- Publisher-subscriber pattern

### Lambda Expressions:
\`\`\`csharp
// Lambda expressions
Func<int, int, int> add = (x, y) => x + y;
Action<string> print = message => Console.WriteLine(message);

// LINQ with lambdas
var filtered = items.Where(x => x.IsActive)
                   .Select(x => x.Name)
                   .ToList();
\`\`\``,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'csharp-async',
        title: 'Asynchronous Programming',
        description: 'Master async/await patterns and Task-based programming',
        content: `## Asynchronous Programming

Learn to build responsive applications with async/await.

### Async Fundamentals:
- Task and Task<T>
- async and await keywords
- ConfigureAwait(false)
- Exception handling in async methods

### Patterns:
- CPU-bound vs I/O-bound operations
- Parallel programming with Task.Run
- Cancellation tokens
- Progress reporting

### Best Practices:
\`\`\`csharp
public async Task<string> FetchDataAsync(string url, CancellationToken cancellationToken = default)
{
    using var client = new HttpClient();
    var response = await client.GetAsync(url, cancellationToken)
                              .ConfigureAwait(false);
    return await response.Content.ReadAsStringAsync()
                         .ConfigureAwait(false);
}
\`\`\``,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 3
      },
      {
        id: 'csharp-linq',
        title: 'LINQ and Expression Trees',
        description: 'Master Language Integrated Query and expression trees',
        content: `## LINQ (Language Integrated Query)

Learn to query data efficiently using LINQ.

### LINQ Operators:
- Filtering: Where, Take, Skip
- Projection: Select, SelectMany
- Grouping: GroupBy, GroupJoin
- Aggregation: Sum, Count, Average
- Ordering: OrderBy, ThenBy

### Query Syntax vs Method Syntax:
\`\`\`csharp
// Query syntax
var result = from p in people
             where p.Age > 18
             orderby p.Name
             select new { p.Name, p.Age };

// Method syntax
var result = people.Where(p => p.Age > 18)
                   .OrderBy(p => p.Name)
                   .Select(p => new { p.Name, p.Age });
\`\`\`

### Expression Trees:
- Compiling expressions at runtime
- Building dynamic queries
- Entity Framework integration`,
        duration: '3 hours',
        difficulty: 'advanced',
        order: 4
      },
      {
        id: 'csharp-reflection',
        title: 'Reflection and Attributes',
        description: 'Learn runtime type inspection and custom attributes',
        content: `## Reflection and Metadata

Explore runtime type information and dynamic programming.

### Reflection Basics:
- Type inspection
- Member discovery
- Dynamic invocation
- Assembly loading

### Attributes:
- Built-in attributes
- Custom attribute creation
- Attribute retrieval
- Conditional compilation

### Use Cases:
\`\`\`csharp
[Serializable]
[CustomValidation("Required")]
public class Person
{
    public string Name { get; set; }
    
    [Obsolete("Use FullName instead")]
    public string GetName() => Name;
}

// Attribute usage
var type = typeof(Person);
var attributes = type.GetCustomAttributes<SerializableAttribute>();
\`\`\``,
        duration: '3 hours',
        difficulty: 'advanced',
        order: 5
      },
      {
        id: 'csharp-memory',
        title: 'Memory Management and Performance',
        description: 'Optimize application performance and memory usage',
        content: `## Memory Management

Understand .NET memory management and optimization techniques.

### Garbage Collection:
- GC generations (0, 1, 2)
- Large Object Heap (LOH)
- GC triggers and collection modes
- Finalizers and IDisposable

### Performance Optimization:
- Span<T> and Memory<T>
- StringBuilders for string manipulation
- Object pooling
- Lazy initialization

### Memory Profiling:
\`\`\`csharp
// Using Span<T> for performance
public void ProcessData(Span<byte> data)
{
    // Work with data without allocation
    var slice = data.Slice(0, 10);
    // Process slice...
}

// IDisposable pattern
public class ResourceManager : IDisposable
{
    private bool disposed = false;
    
    public void Dispose()
    {
        Dispose(true);
        GC.SuppressFinalize(this);
    }
    
    protected virtual void Dispose(bool disposing)
    {
        if (!disposed)
        {
            if (disposing)
            {
                // Dispose managed resources
            }
            disposed = true;
        }
    }
}
\`\`\``,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 6
      },
      {
        id: 'csharp-patterns',
        title: 'Design Patterns in C#',
        description: 'Implement common design patterns using C#',
        content: `## Design Patterns

Learn to apply proven design patterns in C# applications.

### Creational Patterns:
- Singleton pattern
- Factory and Abstract Factory
- Builder pattern
- Dependency Injection

### Structural Patterns:
- Adapter pattern
- Decorator pattern
- Facade pattern
- Proxy pattern

### Behavioral Patterns:
- Observer pattern
- Strategy pattern
- Command pattern
- State pattern

### Example Implementation:
\`\`\`csharp
// Strategy Pattern
public interface IPaymentStrategy
{
    void ProcessPayment(decimal amount);
}

public class CreditCardPayment : IPaymentStrategy
{
    public void ProcessPayment(decimal amount)
    {
        Console.WriteLine($"Processing {amount:C} via Credit Card");
    }
}

public class PaymentContext
{
    private IPaymentStrategy _strategy;
    
    public PaymentContext(IPaymentStrategy strategy)
    {
        _strategy = strategy;
    }
    
    public void ExecutePayment(decimal amount)
    {
        _strategy.ProcessPayment(amount);
    }
}
\`\`\``,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 7
      },
      {
        id: 'csharp-testing',
        title: 'Unit Testing and TDD',
        description: 'Master testing practices with NUnit and Moq',
        content: `## Unit Testing in C#

Learn to write comprehensive unit tests for C# applications.

### Testing Frameworks:
- NUnit fundamentals
- MSTest overview
- xUnit comparison

### Testing Practices:
- Arrange-Act-Assert pattern
- Test naming conventions
- Test categorization
- Parameterized tests

### Mocking with Moq:
\`\`\`csharp
[Test]
public void CalculateTotal_WithValidItems_ReturnsCorrectSum()
{
    // Arrange
    var mockRepository = new Mock<IRepository>();
    mockRepository.Setup(r => r.GetItems())
              .Returns(new List<Item> 
              {
                  new Item { Price = 10.0m },
                  new Item { Price = 20.0m }
              });
    
    var calculator = new PriceCalculator(mockRepository.Object);
    
    // Act
    var result = calculator.CalculateTotal();
    
    // Assert
    Assert.That(result, Is.EqualTo(30.0m));
}
\`\`\``,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 8
      },
      {
        id: 'csharp-netcore',
        title: '.NET Core and Modern C#',
        description: 'Explore .NET Core features and modern C# syntax',
        content: `## .NET Core and Modern C#

Discover the latest features in .NET Core and modern C# versions.

### .NET Core Features:
- Cross-platform development
- Dependency injection container
- Configuration system
- Logging framework
- Host and hosting model

### Modern C# Features:
- Pattern matching
- Record types
- Init-only properties
- Nullable reference types
- Switch expressions

### Example Modern Syntax:
\`\`\`csharp
// Records (C# 9)
public record Person(string FirstName, string LastName)
{
    public string FullName => $"{FirstName} {LastName}";
}

// Pattern matching (C# 8+)
public string GetDiscountDescription(Customer customer) => customer switch
{
    { IsPremium: true, YearsActive: > 5 } => "Premium long-term discount",
    { IsPremium: true } => "Premium discount",
    { YearsActive: > 2 } => "Loyalty discount",
    _ => "No discount"
};

// Init-only properties
public class Product
{
    public string Name { get; init; }
    public decimal Price { get; init; }
}
\`\`\``,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 9
      },
      {
        id: 'csharp-microservices',
        title: 'Microservices with C#',
        description: 'Build scalable microservices architecture',
        content: `## Microservices Architecture

Learn to design and implement microservices using C# and .NET Core.

### Microservices Principles:
- Service decomposition
- Database per service
- API gateway pattern
- Service discovery

### Implementation:
- ASP.NET Core Web API
- Docker containerization
- Message queues (RabbitMQ, Azure Service Bus)
- HTTP client best practices

### Communication Patterns:
- Synchronous (HTTP/REST)
- Asynchronous (messaging)
- Event-driven architecture
- Circuit breaker pattern

### Example Service:
\`\`\`csharp
[ApiController]
[Route("api/[controller]")]
public class OrdersController : ControllerBase
{
    private readonly IOrderService _orderService;
    private readonly IMessagePublisher _publisher;
    
    public OrdersController(IOrderService orderService, IMessagePublisher publisher)
    {
        _orderService = orderService;
        _publisher = publisher;
    }
    
    [HttpPost]
    public async Task<IActionResult> CreateOrder(CreateOrderRequest request)
    {
        var order = await _orderService.CreateOrderAsync(request);
        await _publisher.PublishAsync(new OrderCreatedEvent(order.Id));
        return Ok(order);
    }
}
\`\`\``,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 10
      }
    ],
    type: 'dotnet'
  },

  {
    id: 'csharp-web-development',
    title: 'C# Web Development with ASP.NET Core',
    description: 'Build modern web applications using ASP.NET Core, Entity Framework, and MVC',
    level: 'Intermediate',
    duration: '35 hours',
    totalDuration: '35 hours',
    imageSrc: '/api/placeholder/400/300',
    requirements: ['C# fundamentals', 'HTML/CSS basics', 'Database concepts'],
    learningOutcomes: [
      'Build web applications with ASP.NET Core',
      'Implement MVC architecture pattern',
      'Work with Entity Framework Core',
      'Handle authentication and authorization',
      'Deploy to cloud platforms'
    ],
    modules: [
      {
        id: 'aspnet-intro',
        title: 'Introduction to ASP.NET Core',
        description: 'Get started with ASP.NET Core web development',
        content: `## ASP.NET Core Overview

Learn the fundamentals of modern web development with ASP.NET Core.

### What is ASP.NET Core?
- Cross-platform web framework
- High-performance and scalable
- Built-in dependency injection
- Unified framework for web UI and APIs

### Key Features:
- Modular HTTP request pipeline
- Built-in hosting
- Configuration system
- Logging framework
- Cross-platform deployment

### Project Structure:
- Program.cs - Application entry point
- Startup.cs - Service configuration
- Controllers - Handle HTTP requests
- Views - UI templates
- Models - Data structures`,
        duration: '2 hours',
        difficulty: 'beginner',
        order: 1
      },
      {
        id: 'aspnet-mvc',
        title: 'MVC Architecture Pattern',
        description: 'Master the Model-View-Controller pattern in ASP.NET Core',
        content: `## MVC Pattern in ASP.NET Core

Understand the separation of concerns in web applications.

### MVC Components:
- **Models**: Data and business logic
- **Views**: User interface presentation
- **Controllers**: Handle user input and coordination

### Controller Actions:
\`\`\`csharp
public class HomeController : Controller
{
    public IActionResult Index()
    {
        var model = GetData();
        return View(model);
    }
    
    [HttpPost]
    public IActionResult Create(CreateModel model)
    {
        if (ModelState.IsValid)
        {
            // Process the model
            return RedirectToAction("Index");
        }
        return View(model);
    }
}
\`\`\`

### Routing:
- Conventional routing
- Attribute routing
- Route parameters and constraints`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 2
      },
      {
        id: 'aspnet-views',
        title: 'Razor Views and Templates',
        description: 'Create dynamic web pages with Razor syntax',
        content: `## Razor Views

Learn to create dynamic web pages using Razor syntax.

### Razor Syntax:
- C# code in HTML
- Layout pages and sections
- Partial views
- View components

### Example Razor View:
\`\`\`html
@model List<Product>

<h2>Products</h2>

@if (Model.Any())
{
    <table class="table">
        <thead>
            <tr>
                <th>Name</th>
                <th>Price</th>
                <th>Actions</th>
            </tr>
        </thead>
        <tbody>
            @foreach (var product in Model)
            {
                <tr>
                    <td>@product.Name</td>
                    <td>@product.Price.ToString("C")</td>
                    <td>
                        <a asp-action="Edit" asp-route-id="@product.Id">Edit</a>
                    </td>
                </tr>
            }
        </tbody>
    </table>
}
else
{
    <p>No products found.</p>
}
\`\`\`

### Tag Helpers:
- Built-in tag helpers
- Custom tag helpers
- Form tag helpers`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 3
      },
      {
        id: 'aspnet-ef',
        title: 'Entity Framework Core',
        description: 'Work with databases using Entity Framework Core ORM',
        content: `## Entity Framework Core

Learn to work with databases using the modern ORM for .NET.

### EF Core Features:
- Code-first approach
- Database migrations
- LINQ queries
- Change tracking
- Lazy loading

### DbContext Setup:
\`\`\`csharp
public class ApplicationDbContext : DbContext
{
    public ApplicationDbContext(DbContextOptions<ApplicationDbContext> options)
        : base(options)
    {
    }
    
    public DbSet<Product> Products { get; set; }
    public DbSet<Category> Categories { get; set; }
    
    protected override void OnModelCreating(ModelBuilder modelBuilder)
    {
        modelBuilder.Entity<Product>()
            .HasOne(p => p.Category)
            .WithMany(c => c.Products)
            .HasForeignKey(p => p.CategoryId);
    }
}
\`\`\`

### Queries and Operations:
- CRUD operations
- Complex queries with LINQ
- Relationships and navigation properties
- Bulk operations`,
        duration: '4 hours',
        difficulty: 'intermediate',
        order: 4
      },
      {
        id: 'aspnet-forms',
        title: 'Forms and Validation',
        description: 'Handle user input with forms and validation',
        content: `## Forms and Validation

Learn to handle user input safely and effectively.

### Model Binding:
- Simple types
- Complex objects
- Collections
- Custom model binders

### Validation:
- Data annotations
- Custom validation attributes
- Client-side validation
- Server-side validation

### Example Model with Validation:
\`\`\`csharp
public class RegisterViewModel
{
    [Required(ErrorMessage = "Email is required")]
    [EmailAddress(ErrorMessage = "Invalid email format")]
    public string Email { get; set; }
    
    [Required(ErrorMessage = "Password is required")]
    [StringLength(100, MinimumLength = 6)]
    [DataType(DataType.Password)]
    public string Password { get; set; }
    
    [Compare("Password", ErrorMessage = "Passwords do not match")]
    [DataType(DataType.Password)]
    public string ConfirmPassword { get; set; }
}
\`\`\`

### Form Helpers:
- HTML helpers
- Strongly-typed helpers
- Bootstrap integration`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 5
      },
      {
        id: 'aspnet-api',
        title: 'Web APIs with ASP.NET Core',
        description: 'Build REST APIs and web services',
        content: `## ASP.NET Core Web API

Create robust REST APIs for modern applications.

### API Controller:
\`\`\`csharp
[ApiController]
[Route("api/[controller]")]
public class ProductsController : ControllerBase
{
    private readonly IProductService _productService;
    
    public ProductsController(IProductService productService)
    {
        _productService = productService;
    }
    
    [HttpGet]
    public async Task<ActionResult<IEnumerable<Product>>> GetProducts()
    {
        var products = await _productService.GetAllAsync();
        return Ok(products);
    }
    
    [HttpGet("{id}")]
    public async Task<ActionResult<Product>> GetProduct(int id)
    {
        var product = await _productService.GetByIdAsync(id);
        if (product == null)
            return NotFound();
        return Ok(product);
    }
    
    [HttpPost]
    public async Task<ActionResult<Product>> CreateProduct(CreateProductDto dto)
    {
        var product = await _productService.CreateAsync(dto);
        return CreatedAtAction(nameof(GetProduct), new { id = product.Id }, product);
    }
}
\`\`\`

### API Features:
- Content negotiation
- Model validation
- Error handling
- API versioning
- OpenAPI/Swagger documentation`,
        duration: '4 hours',
        difficulty: 'intermediate',
        order: 6
      },
      {
        id: 'aspnet-auth',
        title: 'Authentication and Authorization',
        description: 'Secure your applications with authentication and authorization',
        content: `## Security in ASP.NET Core

Implement comprehensive security for your web applications.

### Authentication:
- Cookie authentication
- JWT bearer tokens
- External providers (Google, Facebook)
- Identity framework

### Authorization:
- Role-based authorization
- Claims-based authorization
- Policy-based authorization
- Resource-based authorization

### Identity Setup:
\`\`\`csharp
// In Startup.cs
services.AddIdentity<ApplicationUser, IdentityRole>()
    .AddEntityFrameworkStores<ApplicationDbContext>()
    .AddDefaultTokenProviders();

services.Configure<IdentityOptions>(options =>
{
    options.Password.RequireDigit = true;
    options.Password.RequireLowercase = true;
    options.Password.RequireNonAlphanumeric = false;
    options.Password.RequireUppercase = true;
    options.Password.RequiredLength = 6;
});

// Authorization policies
services.AddAuthorization(options =>
{
    options.AddPolicy("AdminOnly", policy =>
        policy.RequireRole("Admin"));
    
    options.AddPolicy("MinimumAge", policy =>
        policy.RequireAssertion(context =>
            context.User.HasClaim("age", age => int.Parse(age) >= 18)));
});
\`\`\``,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 7
      },
      {
        id: 'aspnet-middleware',
        title: 'Middleware and Request Pipeline',
        description: 'Understand and customize the ASP.NET Core request pipeline',
        content: `## Middleware Pipeline

Learn to customize the request processing pipeline.

### Middleware Concepts:
- Request processing pipeline
- Built-in middleware
- Custom middleware
- Middleware order importance

### Custom Middleware:
\`\`\`csharp
public class RequestLoggingMiddleware
{
    private readonly RequestDelegate _next;
    private readonly ILogger<RequestLoggingMiddleware> _logger;
    
    public RequestLoggingMiddleware(RequestDelegate next, ILogger<RequestLoggingMiddleware> logger)
    {
        _next = next;
        _logger = logger;
    }
    
    public async Task InvokeAsync(HttpContext context)
    {
        _logger.LogInformation($"Request: {context.Request.Method} {context.Request.Path}");
        
        await _next(context);
        
        _logger.LogInformation($"Response: {context.Response.StatusCode}");
    }
}

// Extension method
public static class MiddlewareExtensions
{
    public static IApplicationBuilder UseRequestLogging(this IApplicationBuilder builder)
    {
        return builder.UseMiddleware<RequestLoggingMiddleware>();
    }
}
\`\`\`

### Common Middleware:
- Exception handling
- HTTPS redirection
- Static files
- Authentication
- CORS`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 8
      },
      {
        id: 'aspnet-testing',
        title: 'Testing ASP.NET Core Applications',
        description: 'Write comprehensive tests for web applications',
        content: `## Testing Web Applications

Learn to test ASP.NET Core applications effectively.

### Testing Types:
- Unit tests
- Integration tests
- End-to-end tests
- API testing

### Test Setup:
\`\`\`csharp
public class ProductsControllerTests : IClassFixture<WebApplicationFactory<Program>>
{
    private readonly WebApplicationFactory<Program> _factory;
    private readonly HttpClient _client;
    
    public ProductsControllerTests(WebApplicationFactory<Program> factory)
    {
        _factory = factory;
        _client = _factory.CreateClient();
    }
    
    [Fact]
    public async Task GetProducts_ReturnsSuccessAndCorrectContentType()
    {
        // Act
        var response = await _client.GetAsync("/api/products");
        
        // Assert
        response.EnsureSuccessStatusCode();
        Assert.Equal("application/json; charset=utf-8", 
            response.Content.Headers.ContentType.ToString());
    }
}
\`\`\`

### Testing Tools:
- TestServer for integration tests
- Moq for mocking
- FluentAssertions for readable assertions
- WebApplicationFactory for testing`,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 9
      },
      {
        id: 'aspnet-performance',
        title: 'Performance and Optimization',
        description: 'Optimize ASP.NET Core application performance',
        content: `## Performance Optimization

Learn to build high-performance web applications.

### Performance Techniques:
- Response caching
- Output caching
- Compression
- Static file optimization
- Database query optimization

### Caching Strategies:
\`\`\`csharp
// In-memory caching
public class ProductService
{
    private readonly IMemoryCache _cache;
    
    public async Task<List<Product>> GetProductsAsync()
    {
        if (!_cache.TryGetValue("products", out List<Product> products))
        {
            products = await _repository.GetAllAsync();
            _cache.Set("products", products, TimeSpan.FromMinutes(30));
        }
        return products;
    }
}

// Distributed caching
services.AddStackExchangeRedisCache(options =>
{
    options.Configuration = "localhost:6379";
});
\`\`\`

### Monitoring:
- Application Insights
- Performance counters
- Custom metrics
- Health checks`,
        duration: '3 hours',
        difficulty: 'advanced',
        order: 10
      },
      {
        id: 'aspnet-deployment',
        title: 'Deployment and DevOps',
        description: 'Deploy ASP.NET Core applications to production',
        content: `## Deployment Strategies

Learn to deploy ASP.NET Core applications to various platforms.

### Deployment Targets:
- IIS on Windows
- Linux with Nginx
- Docker containers
- Azure App Service
- AWS Elastic Beanstalk

### Configuration Management:
- appsettings.json environments
- Environment variables
- Azure Key Vault
- Configuration providers

### CI/CD Pipeline:
\`\`\`yaml
# GitHub Actions example
name: Deploy to Azure

on:
  push:
    branches: [main]

jobs:
  build-and-deploy:
    runs-on: ubuntu-latest
    
    steps:
    - uses: actions/checkout@v2
    
    - name: Setup .NET
      uses: actions/setup-dotnet@v1
      with:
        dotnet-version: 6.0.x
        
    - name: Restore dependencies
      run: dotnet restore
      
    - name: Build
      run: dotnet build --no-restore
      
    - name: Test
      run: dotnet test --no-build --verbosity normal
      
    - name: Publish
      run: dotnet publish -c Release -o ./publish
      
    - name: Deploy to Azure
      uses: azure/webapps-deploy@v2
      with:
        app-name: 'my-app'
        publish-profile: \${{ secrets.AZURE_WEBAPP_PUBLISH_PROFILE }}
        package: './publish'
\`\`\``,
        duration: '3 hours',
        difficulty: 'intermediate',
        order: 11
      },
      {
        id: 'aspnet-microservices',
        title: 'Microservices Architecture',
        description: 'Build distributed systems with ASP.NET Core microservices',
        content: `## Microservices with ASP.NET Core

Design and implement microservices architecture.

### Architecture Patterns:
- API Gateway
- Service discovery
- Circuit breaker
- Event sourcing
- CQRS pattern

### Communication:
- HTTP/REST APIs
- gRPC services
- Message brokers
- Event-driven architecture

### Example Service:
\`\`\`csharp
// Order service
[ApiController]
[Route("api/orders")]
public class OrdersController : ControllerBase
{
    private readonly IOrderService _orderService;
    private readonly IEventBus _eventBus;
    
    [HttpPost]
    public async Task<IActionResult> CreateOrder(CreateOrderCommand command)
    {
        var order = await _orderService.CreateOrderAsync(command);
        
        await _eventBus.PublishAsync(new OrderCreatedEvent
        {
            OrderId = order.Id,
            CustomerId = order.CustomerId,
            Total = order.Total
        });
        
        return CreatedAtAction(nameof(GetOrder), new { id = order.Id }, order);
    }
}
\`\`\`

### Tools and Technologies:
- Docker containerization
- Kubernetes orchestration
- Service mesh (Istio)
- Monitoring and logging`,
        duration: '4 hours',
        difficulty: 'advanced',
        order: 12
      }
    ],
    type: 'dotnet'
  }
];